import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as GameShape } from '../../../assets/graphics/svg/colorable/BubbleShape_Game.svg';
import { getGameTypeDetails } from '../../../constants/Games/gameType';
import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import { IconType, Icons } from '../../../constants/icons';
import { Images } from '../../../constants/images';
import { getPathBGByIndex } from '../../../constants/pathBackground';
import { SpaceType } from '../../../constants/space';
import { colorForGameSetId } from '../../../constants/Theme/DefaultColors';
import { Space } from '../../../models/SpaceStore';
import { Album, AlbumAudience } from '../../../models/SpaceStore/Album';
import { Profile } from '../../../models/UserStore';
import { timeAgo } from '../../../utils/date';
import { AvatarNode } from '../AvatarNode';
import { Card } from '../Card';
import { SeparatorComponent } from '../SeparatorComponent';
import { Skeleton } from '../Skeleton';
import { SpeakingContentView } from '../SpeakingContentView';
import { SvgIcon } from '../SVGIcon';

type AlbumViewCardProps = {
  album: Album | undefined;
  albumClassroom: Space | undefined;
  classroomOwnerProfile?: Profile | undefined | null;
  game?: {
    name: string;
    gameTypeIcon: IconType;
    backgroundImageSource: string | undefined;
  };
  backgroundColor?: string;
};

export const AlbumViewCard = memo((props: AlbumViewCardProps) => {
  const {
    album,
    albumClassroom,
    game,
    classroomOwnerProfile,
    backgroundColor,
  } = props;
  const { t, i18n } = useTranslation();

  const albumInfo =
    album?.i18nGameSetInfo.resources[album?.i18nGameSetInfo.defaultLocale];
  const gameTypeIcons = album?.content?.partIcons
    ? new Set(
        Object.values(album?.content?.partIcons)
          .filter((iconName) => iconName !== null)
          .map((iconName) =>
            iconName
              ? getGameTypeDetails(iconName).icon ?? IconType.GameLine
              : IconType.GameLine,
          ),
      )
    : undefined;
  const [isAlbumClassroomIconAvailable, setIsAlbumClassroomIconAvailable] =
    useState(true);

  const albumIcon = getPathBGByIndex(
    album?.content?.path[0]?.style.background.set ?? 0,
  );
  return (
    <Card
      style={{
        backgroundColor: backgroundColor || '#f2f2f2',
        gap: '0.5rem',
        width: '100%',
        padding: '0rem 1rem',
        margin: ' 0 1rem',
        maxWidth: '456px',
      }}
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{
          aspectRatio: '1 / 1',
          width: '90%',
          position: 'relative',
          borderRadius: '50%',
          margin: '1rem',
        }}
      >
        {/* <GameShape fill="#cccccc" width={'100%'} /> */}
        {/* <ShowShape fill="#cccccc" width={'100%'} /> */}
        <GameShape
          fill={colorForGameSetId(album?.gameSetId ?? '')}
          style={{ position: 'absolute', width: '100%', height: '100%' }}
        />

        {game ? (
          <SpeakingContentView
            backgroundImageSource={albumIcon ?? game.backgroundImageSource}
          />
        ) : album ? (
          <>
            <img
              src={
                album.icons[0]?.publicUrls?.medium ??
                albumIcon ??
                Images.speakingGameBackground
              }
              alt="Background"
              style={{
                position: 'absolute',
                aspectRatio: '1 / 1',
                width: '100%',
                objectFit: 'cover',
                borderRadius: '50%',
                padding: '0.75rem',
                overflow: 'hidden',
              }}
            />
            <div
              className="flex flex-col items-center justify-center"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                padding: '0.5rem',
              }}
            >
              <p
                className="text-3xl font-bold"
                style={{
                  color: 'white',
                  zIndex: 2,
                  textAlign: 'center',
                  padding: '1rem',
                  textShadow: '2px 2px 10px rgba(0, 0, 0, 0.5)',
                }}
              >
                {albumInfo?.name}
              </p>
            </div>
          </>
        ) : null}

        <div
          className="flex justify-center"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            alignItems: 'flex-end',
            padding: '0.5rem',
          }}
        >
          <div
            className="flex  overflow-hidden"
            style={{
              minHeight: 35,
              borderRadius: '0.5rem',
            }}
          >
            {gameTypeIcons && gameTypeIcons?.size > 0 ? (
              <div
                className="flex items-center gap-1"
                style={{
                  backgroundColor: '#00000030',
                  padding: '0 0.5rem',
                }}
              >
                {(game
                  ? [game.gameTypeIcon]
                  : Array.from(gameTypeIcons).slice(0, 5)
                ).map((iconType) => (
                  <img
                    key={iconType}
                    className="size-icon-s"
                    src={Icons[iconType]}
                    alt={iconType}
                  />
                ))}
              </div>
            ) : null}

            {!game &&
            album &&
            album.statistics.itemCount &&
            album.statistics.itemCount > 0 ? (
              <div
                style={{
                  backgroundColor: '#00000050',
                  padding: '0.25rem',
                  minWidth: '2rem',
                }}
              >
                <p
                  className="text-xl"
                  style={{
                    color: 'white',
                    zIndex: 2,
                    textAlign: 'center',
                  }}
                >
                  {album.statistics.itemCount}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <hr />
      <div
        className="flex flex-col gap-1"
        style={{
          justifyContent: 'start',
          width: '100%',
          padding: '0.5rem',
        }}
      >
        <p className="text-md" style={{ color: '#333333' }}>
          {game?.name ?? albumInfo?.name}
        </p>
        <div className="flex items-center gap-1" style={{ flexWrap: 'wrap' }}>
          <SeparatorComponent
            separator={<p className="text-gray-600 text-sm">·</p>}
          >
            <div className="flex flex-row items-center gap-1">
              {classroomOwnerProfile &&
              albumClassroom?.type == SpaceType.Home ? (
                <>
                  <AvatarNode
                    avatar={{
                      ...classroomOwnerProfile.avatar,
                      userId: classroomOwnerProfile.userId,
                      source:
                        classroomOwnerProfile.profilePicture &&
                        classroomOwnerProfile.profilePicture.publicUrls?.small
                          ? {
                              uri: classroomOwnerProfile.profilePicture
                                .publicUrls.small,
                            }
                          : NOT_APPLICABLE_STRING,
                    }}
                    size={32}
                  />
                </>
              ) : albumClassroom?.icon?.publicUrls?.small &&
                isAlbumClassroomIconAvailable ? (
                <img
                  src={albumClassroom?.icon?.publicUrls?.small}
                  onError={() => setIsAlbumClassroomIconAvailable(false)}
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '1px solid #cccccc50',
                    // borderRadius: '0.5rem 0.5rem 0.25rem 0.25rem',
                    borderRadius: '1rem',
                    aspectRatio: 1,
                  }}
                />
              ) : (
                <Skeleton
                  width={'32px'}
                  height={'32px'}
                  // borderRadius={'0.5rem 0.5rem 0.25rem 0.25rem'}
                  borderRadius={'1rem'}
                />
              )}

              <p className="text-gray-600 text-sm">
                {
                  albumClassroom?.i18nClassroomInfo.resources[
                    albumClassroom.i18nClassroomInfo.defaultLocale
                  ]?.name
                }
              </p>
            </div>
            {album ? (
              <p className="text-gray-600 text-sm">
                {timeAgo(album.createdAt, i18n.language)}
              </p>
            ) : null}

            {album ? (
              <SvgIcon
                name={
                  album.audience == AlbumAudience.Public
                    ? 'Region'
                    : album.audience == AlbumAudience.Members
                    ? 'Member'
                    : album.audience == AlbumAudience.Followers
                    ? 'Friend'
                    : 'MembershipTier'
                }
                width={20}
                height={20}
                stroke="#808080"
              />
            ) : null}

            {album &&
            album.statistics.gamePlayCount &&
            album.statistics.gamePlayCount > 0 ? (
              <p className="text-gray-600 text-sm">
                {t('{count} Plays', {
                  count: album.statistics.gamePlayCount,
                })}
              </p>
            ) : null}
          </SeparatorComponent>
        </div>
        <p className="text-gray-600 text-sm">{albumInfo?.description}</p>
      </div>
    </Card>
  );
});
